import { safePersistStorage } from './safePersistStorage';
import type { StorageValue } from 'zustand/middleware';

/**
 * localStorage with cross-tab sync for Zustand persist middleware.
 *
 * Wraps `createJSONStorage` (handles JSON serialization) and adds a `subscribe`
 * method that uses the browser's `storage` event to keep state in sync across
 * browser tabs.
 *
 * Usage:
 * ```ts
 * persist(storeCreator, {
 *   name: 'my-store',
 *   storage: crossTabStorage,
 * })
 * ```
 *
 * The storage itself is `safePersistStorage('local')` — resolved on first use,
 * with a memory fallback. See that module for why both matter; this one only
 * adds the cross-tab `subscribe` on top.
 */
const base = safePersistStorage<unknown>('local');
export const crossTabStorage = {
  getItem: (name: string) => base.getItem(name),
  setItem: (name: string, value: StorageValue<unknown>) =>
    base.setItem(name, value),
  removeItem: (name: string) => base.removeItem(name),
  subscribe: (key: string, callback: (value: string | null) => void) => {
    // No `window` means no other tabs to sync with, so there is nothing to
    // subscribe to — return a no-op teardown rather than throwing.
    if (typeof window === 'undefined') return () => {};
    const handler = (e: StorageEvent) => {
      if (e.key === key) callback(e.newValue);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  },
};
